import Link from "next/link";

import { ADDRESS, APP_LOGIN, COMPANY, CONTACT_EMAIL, nav, tagline } from "@/lib/site";
import type { Section } from "@/lib/site";

const Chevron = () => (
  <svg viewBox="0 0 12 12" fill="none" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 4.5l3 3 3-3" />
  </svg>
);

const DROPDOWNS: { id: Exclude<Section, "legal">; label: string }[] = [
  { id: "product", label: "Product" },
  { id: "solutions", label: "Solutions" },
  { id: "company", label: "Company" },
];

const SUB_LABEL: Record<Section, string> = {
  product: "Platform",
  solutions: "Solutions",
  company: "Company",
  legal: "Legal",
};

/** Top bar. Privacy and Terms stay visible at every width, never only inside a menu. */
export function SiteHeader({ section, active }: { section?: Section; active?: string }) {
  return (
    <div className="sa hd-root">
      <header className="hd">
        <div className="wrap">
          <Link className="logo" href="/" aria-label="Fitted home">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/fitted-logo.svg" alt="Fitted" />
          </Link>
          <nav className="main" aria-label="Main">
            {DROPDOWNS.map((d) => (
              <div className="dd" key={d.id}>
                <button type="button" aria-haspopup="true">
                  {d.label}
                  <Chevron />
                </button>
                <div className="panel">
                  {nav[d.id].map((item) => (
                    <Link key={item.href} href={item.href}>
                      <b>{item.label}</b>
                      {item.line && <span>{item.line}</span>}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </nav>
          <div className="right">
            <div className="legal">
              <Link href="/privacy-policy">Privacy</Link>
              <Link href="/terms-of-service">Terms</Link>
            </div>
            <a href={APP_LOGIN}>Log in</a>
            <Link className="btn p sm" href="/request-access">
              Request access
            </Link>
          </div>
          <div className="menu">
            <Link className="btn p sm" href="/request-access">
              Request access
            </Link>
            <details className="mm">
              <summary aria-label="Menu">
                <svg className="ham" width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#0F1A1C" strokeWidth="1.6" aria-hidden="true">
                  <path d="M3 6h12M3 12h12" />
                </svg>
                <svg className="x" width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#0F1A1C" strokeWidth="1.6" aria-hidden="true">
                  <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" />
                </svg>
              </summary>
              <div className="sheet">
                {DROPDOWNS.map((d) => (
                  <div key={d.id}>
                    <h4>{d.label}</h4>
                    {nav[d.id].map((item) => (
                      <Link key={item.href} href={item.href}>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                ))}
                <div className="row">
                  <a className="btn g sm" href={APP_LOGIN}>
                    Log in
                  </a>
                  <Link className="btn p sm" href="/request-access">
                    Request access
                  </Link>
                </div>
              </div>
            </details>
          </div>
        </div>
      </header>
      <div className="hd-legal">
        <Link href="/privacy-policy">Privacy Policy</Link>
        <Link href="/terms-of-service">Terms of Service</Link>
        <span>
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </span>
      </div>
      {section && <SubNav section={section} active={active} />}
    </div>
  );
}

function SubNav({ section, active }: { section: Section; active?: string }) {
  return (
    <nav className="sub" aria-label={SUB_LABEL[section]}>
      <div className="wrap">
        <b>{SUB_LABEL[section]}</b>
        {nav[section].map((item) => (
          <Link key={item.href} href={item.href} className={item.href === active ? "on" : undefined} aria-current={item.href === active ? "page" : undefined}>
            {item.label === "Platform overview" ? "Overview" : item.label}
          </Link>
        ))}
      </div>
    </nav>
  );
}

export function SiteFooter() {
  const cols: { title: string; items: { label: string; href: string }[] }[] = [
    { title: "Product", items: nav.product },
    { title: "Solutions", items: nav.solutions },
    { title: "Company", items: [...nav.company, { label: "Log in", href: APP_LOGIN }] },
    { title: "Legal", items: nav.legal },
  ];
  return (
    <div className="sa">
      <footer className="ft">
        <div className="wrap">
          <div className="cols">
            <div className="co">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/fitted-logo.svg" alt="Fitted" />
              <p>{tagline}</p>
              <p>
                {COMPANY}
                <br />
                {ADDRESS[0]}
                <br />
                {ADDRESS[1]}
                <br />
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </p>
            </div>
            {cols.map((c) => (
              <div key={c.title}>
                <h4>{c.title}</h4>
                <ul>
                  {c.items.map((i) => (
                    <li key={i.href}>
                      {i.href.startsWith("http") ? <a href={i.href}>{i.label}</a> : <Link href={i.href}>{i.label}</Link>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="base">
            <span>© {new Date().getFullYear()} {COMPANY}</span>
            <span>
              {nav.legal.map((l) => (
                <Link key={l.href} href={l.href}>
                  {l.label}
                </Link>
              ))}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
